import React, { useMemo, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import block from 'bem-cn';
import SVGInline from 'react-svg-inline';

import { actions } from 'features/Game';
import { addNotify } from 'features/Notify/redux/actions/index';
import { maxBallsCount } from 'components/GameView/initials';
import Button from 'components/Button';
import Input from 'components/Input';
import Selector from 'components/Selector';

import minusSvg from './img/minus.svg';
import plusSvg from './img/plus.svg';

import './Basket.scss';

const b = block('basket');

const Basket = () => {
  const dispatch = useDispatch();
  const { amount } = useSelector(state => state.game.gameSettings);
  const { riskLevel } = useSelector(state => state.game.gameSettings);
  const ballsCount = useSelector(state => state.game.gameSettings.ballsCount);
  const { rowsCount } = useSelector(state => state.game.gameSettings);
  const { activeBalls } = useSelector(state => state.game);
  const locale = useSelector(state => state.locale.locale);
  const balance = useSelector(state => state.auth.balance);
  const limits = useSelector(state => state.game.limits);
  const maxRows = 16;
  const minRows = 8;
  const setAmount = useCallback(amount => dispatch(actions.setGameSettings({ amount })), [dispatch])
  const setRiskLevel = useCallback(riskLevel => dispatch(actions.setGameSettings({ riskLevel })), [dispatch])
  const setBallsCount = useCallback(ballsCount => dispatch(actions.setGameSettings({ ballsCount })), [dispatch])
  const setRowsCount = useCallback(rowsCount => dispatch(actions.setGameSettings({ rowsCount })), [dispatch])
  const addNotifyOverflow = useCallback(() => dispatch(addNotify(locale.errorMessages[10], 'error')), [dispatch, locale.errorMessages]);

  // Ball count
  const ballsCountOptions = useMemo(() => Array(Math.min(maxBallsCount, Math.trunc(limits.max / amount))).fill('').map((a, i) => ({ value: i + 1, text: i + 1 })), [amount, limits.max]);

  const mapRiskLevelNumperIntoString = {
    0: `${locale.lowRisk}`,
    1: `${locale.normalRisk}`,
    2: `${locale.highRisk}`,
  }

  const placeBet = useCallback(() => {
    dispatch(actions.startGame())
  }, [dispatch]);

  const rowsOptions = useMemo(() => {
    let result = [];
    for (let i = minRows; i <= maxRows; i++) {
      result = [...result, { value: i, text: i }]
    }
    return result;
  }, []);

  const riskOption = [{ value: 0, text: `${locale.lowRisk}` }, { value: 1, text: `${locale.normalRisk}` }, { value: 2, text: `${locale.highRisk}` }];

  // Set min amount
  useEffect(() => {
    setAmount(limits.min)
  }, [limits.min, setAmount]);

  // Button text
  const buttonText = locale.startGame;

  // For validate amount

  const validateChangeAmount = useCallback(value => {
    let normalizeValue = value;

    if (value < limits.min) normalizeValue = +limits.min.toFixed(1)
    else if (value > limits.max) normalizeValue = +limits.max.toFixed(1)
    else if (+(+value).toFixed(1) > balance) {
      if (balance < limits.min) {
        setAmount(+limits.min.toFixed(1))
      } else {
        setAmount(Math.floor(balance * 10, 1) / 10)
      }
    }
    else normalizeValue = +(+value).toFixed(1);

    if (normalizeValue * ballsCount > limits.max) {
      addNotifyOverflow();
      return;
    }
    setAmount(normalizeValue);
  }, [balance, ballsCount, limits.max, limits.min, setAmount, addNotifyOverflow]);

  // For validate input Amount

  const validateTempAmount = useCallback(value => {
    if ((!isNaN(+value) || value.toString().indexOf('.') === value.toString().length - 1) &&
      value.toString().split('').filter(t => t === '.').length < 2) {
      if (value.toString().split('')[0] === '.') {
        setAmount(value.substring(1, value.length))
      } else setAmount(value)
    }
  }, [setAmount])

  return <footer className={b('footer')}>
    <div className={b('container')}>
      <Selector
        callBack={setRowsCount}
        options={rowsOptions}
        active={{ text: `${locale.rows}:  ${rowsCount}`, value: rowsCount }}
        isDisabled={Boolean(activeBalls.length)}
      />
    </div>
    <div className={b('container')}>
      <Selector
        callBack={setRiskLevel}
        options={riskOption}
        active={{ value: riskLevel, text: `${locale.risk}:  ${mapRiskLevelNumperIntoString[riskLevel]}` }}
        isDisabled={Boolean(activeBalls.length)}
      />
    </div>
    <div className={b('container')}>
      <Selector
        callBack={setBallsCount}
        options={ballsCountOptions}
        active={{ value: ballsCount, text: `${locale.balls}: ${ballsCount}` }}
        isDisabled={Boolean(activeBalls.length)}
      />
    </div>
    <div className={b('container')}>
      <div className={b('change-container')}>
        <div className={b('change-block')} onClick={() => validateChangeAmount(amount - 10 * limits.min)}>
          <SVGInline svg={minusSvg} className={b('change-icon').toString()} />
        </div>
        <div className={b('change-block')}>
          <Input
            value={amount}
            onChange={e => validateTempAmount(e.currentTarget.value)}
            onBlur={() => validateChangeAmount(amount)}
            unBorder
            centerText />
        </div>
        <div className={b('change-block')} onClick={() => validateChangeAmount(amount + 10 * limits.min)}>
          <SVGInline svg={plusSvg} className={b('change-icon').toString()} />
        </div>
      </div>
    </div>
    <div className={b('container-default-button')}>
      <div className={b('bet-block')} onClick={() => validateChangeAmount(limits.min)}>
        {locale.min}
      </div>
      <div className={b('bet-block')} onClick={() => validateChangeAmount(amount * 2)}>
        x2
      </div>
      <div className={b('bet-block')} onClick={() => validateChangeAmount(balance)}>
        {locale.max}
      </div>
    </div>
    <div className={b('main-button')}>
      <Button
        callBack={placeBet}
        text={buttonText}
        size='container'
        isUppercase
        color='orange'
        fontSize='normal' />
    </div>
  </footer>
}

export default Basket;